// La frise en haut de la fiche chantier : du devis à l'encaissement.
//
// Le raisonnement vit dans @imp/shared/frise-chantier, pur et testé ; ce
// fichier ne fait que le mettre en page. L'étape en cours est mise en
// évidence, avec à côté le bouton qui la fait avancer.

import { type EtapeFrise, type Frise } from '@imp/shared/frise-chantier';
import { ArrowRight, Check, HelpCircle, Minus } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

function Pastille({ etape }: { etape: EtapeFrise }) {
  const base = 'bg-card flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2';
  switch (etape.etat) {
    case 'fait':
      return (
        <span className={cn(base, 'border-money-in bg-money-in text-white')}>
          <Check className="h-4 w-4" strokeWidth={3} />
        </span>
      );
    case 'en_cours':
      return (
        <span className={cn(base, 'border-action ring-action/25 ring-4')}>
          <span className="bg-action h-2.5 w-2.5 rounded-full" />
        </span>
      );
    case 'sans_objet':
      return (
        <span className={cn(base, 'border-muted-foreground/30 text-muted-foreground/60')}>
          <Minus className="h-3.5 w-3.5" />
        </span>
      );
    case 'inconnu':
      return (
        <span
          className={cn(base, 'border-muted-foreground/30 text-muted-foreground/60 border-dashed')}
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </span>
      );
    case 'a_venir':
      return <span className={cn(base, 'border-muted-foreground/40')} />;
  }
}

export function FriseChantier({
  frise,
  indisponible,
}: {
  frise: Frise | null;
  /** Les devis ou factures n'ont pas pu être lus : rien n'est affirmé. */
  indisponible: boolean;
}) {
  const liste = useRef<HTMLOListElement>(null);
  const enCours = frise?.enCours ?? null;
  // Sur un téléphone, la frise défile en largeur : on l'amène sur l'étape en
  // cours, qui sinon pourrait rester hors de l'écran, à droite.
  useEffect(() => {
    const ol = liste.current;
    const li = ol?.querySelector<HTMLElement>('[aria-current="step"]');
    if (!ol || !li) return;
    ol.scrollLeft = li.offsetLeft - (ol.clientWidth - li.clientWidth) / 2;
  }, [enCours]);

  if (frise === null)
    return (
      <Card>
        <CardContent className="text-muted-foreground py-4 text-sm">
          {indisponible
            ? 'Impossible de retracer le chantier pour le moment : le message en haut de la page dit ce qui n’a pas pu se charger.'
            : 'Lecture du chantier…'}
        </CardContent>
      </Card>
    );

  const courante = frise.etapes.find((e) => e.id === frise.enCours) ?? null;

  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <ol
          ref={liste}
          className="relative flex gap-1 overflow-x-auto pb-1"
          aria-label="Avancement du chantier"
        >
          {frise.etapes.map((e, i) => (
            <li
              key={e.id}
              aria-current={e.etat === 'en_cours' ? 'step' : undefined}
              className="relative flex min-w-[96px] flex-1 flex-col items-center text-center"
            >
              {/* Le trait qui relie à l'étape précédente. */}
              {i > 0 ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    'absolute right-1/2 top-3.5 -z-0 h-0.5 w-full',
                    e.etat === 'fait' ? 'bg-money-in' : 'bg-muted-foreground/20',
                  )}
                />
              ) : null}
              <span className="relative z-10">
                <Pastille etape={e} />
              </span>
              <span
                className={cn(
                  'mt-1.5 text-xs font-semibold',
                  e.etat === 'en_cours' ? 'text-action' : '',
                  e.etat === 'sans_objet' || e.etat === 'inconnu' ? 'text-muted-foreground' : '',
                )}
              >
                {e.libelle}
              </span>
              <span className="text-muted-foreground mt-0.5 text-[11px] leading-tight">
                {e.etat === 'inconnu' ? 'hors CRM' : e.detail}
              </span>
            </li>
          ))}
        </ol>

        {courante !== null ? (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3">
            <p className="text-sm">
              <span className="text-muted-foreground">Étape en cours : </span>
              <strong>{courante.libelle}</strong>
              {courante.detail !== null ? (
                <span className="text-muted-foreground"> — {courante.detail}</span>
              ) : null}
            </p>
            {courante.action !== null ? (
              <Button asChild size="sm">
                <Link to={courante.action.lien}>
                  {courante.action.libelle} <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            ) : null}
          </div>
        ) : (
          <p className="text-money-in border-t pt-3 text-sm font-medium">
            Chantier soldé : toutes les étapes sont franchies.
          </p>
        )}

        {frise.horsCrm ? (
          <p className="text-muted-foreground text-xs">
            Aucun devis ni aucune facture de ce chantier dans le CRM (sans doute dans Extrabat) :
            les étapes commerciales ne peuvent pas s’afficher. Elles se remplissent seules dès qu’un
            devis ou une facture du CRM est rattaché au chantier.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
